import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useUploadFileAction } from "../action/use-upload-file-action";
import type { FileDto, GetKitFolderResponseDto } from "../lib/gen-api";
import {
  fileControllerDeleteFile,
  fileControllerParseFile,
  fileControllerUpdateFile,
  groupKitFolderControllerGetFolder,
  groupKitFolderControllerSync,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

const FOLDER_POLLING_INTERVAL = 5000;

const flattenFiles = (data: GetKitFolderResponseDto): FileDto[] => {
  const files = Object.values(data.folders).flatMap((f) => f.files);
  return [...data.folder.files, ...files];
};

/** True while parse may still start or is in progress (not done / failed / tombstoned). */
function fileNeedsStatusPolling(file: FileDto): boolean {
  if (file.dateDeleted) {
    return false;
  }
  if (file.isParsing) {
    return true;
  }
  // Gap between queueFileParse and brain parseStart: is_uploaded, not yet
  // isParsing / isSearchable — without this, link/sync land on "Uploaded" forever.
  return file.is_uploaded && !file.isSearchable && !file.error;
}

export function useFolderData(
  groupUuid: string | null,
  kitUuid: string | null,
  folderUuid: string | null,
  options?: {
    enabled?: boolean;
  },
) {
  const queryClient = useQueryClient();
  const { uploadFile } = useUploadFileAction(groupUuid, kitUuid);
  const enabled = options?.enabled ?? true;
  const { data, isPending: isFolderLoading } = useQuery({
    enabled: enabled && !!groupUuid && !!kitUuid && !!folderUuid,
    queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
    queryFn: async () => {
      const response = await groupKitFolderControllerGetFolder({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          folderUuid: folderUuid!,
        },
      });
      return response.data;
    },
    refetchInterval: (query) => {
      const response = query.state.data;
      if (!response) return false;

      const allFiles = flattenFiles(response);
      if (!allFiles.some(fileNeedsStatusPolling)) {
        return false;
      }

      return FOLDER_POLLING_INTERVAL;
    },
  });

  const { mutateAsync: createFile, isPending: isCreatingFile } = useMutation({
    mutationFn: async (file: File) => {
      if (!groupUuid || !kitUuid || !folderUuid) {
        throw new Error("Group UUID, Kit UUID, and Folder UUID are required");
      }

      return await uploadFile({ file, folderUuid });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: replaceFile, isPending: isReplacingFile } = useMutation({
    mutationFn: async (params: { fileUuid: string; file: File }) => {
      if (!groupUuid || !kitUuid || !folderUuid) {
        throw new Error("Group UUID, Kit UUID, and Folder UUID are required");
      }

      return await uploadFile({
        file: params.file,
        folderUuid,
        fileUuid: params.fileUuid,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: deleteFile } = useMutation({
    mutationFn: (fileUuid: string) => {
      if (!groupUuid || !kitUuid || !folderUuid) {
        throw new Error("Group UUID, Kit UUID, and Folder UUID are required");
      }

      return fileControllerDeleteFile({
        path: {
          fileUuid,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: updateFile } = useMutation({
    mutationFn: async (params: {
      fileUuid: string;
      body: {
        name?: string;
        folderUuid?: string;
      };
    }) => {
      if (!groupUuid || !kitUuid || !folderUuid) {
        throw new Error("Group UUID, Kit UUID, and Folder UUID are required");
      }

      return await fileControllerUpdateFile({
        path: {
          fileUuid: params.fileUuid,
        },
        body: params.body,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
      });
    },
  });

  const { mutateAsync: parseRemainingFiles } = useMutation({
    mutationFn: async () => {
      if (!groupUuid || !kitUuid || !folderUuid) {
        throw new Error("Group UUID, Kit UUID, and Folder UUID are required");
      }

      if (!data) {
        return;
      }

      const filesToParse = flattenFiles(data).filter(
        (file) => file.isParsing === false && file.isSearchable === false,
      );

      for (const file of filesToParse) {
        await fileControllerParseFile({
          path: {
            fileUuid: file.uuid,
          },
        });
      }
    },
    onSettled: () => {
      // Allow reasonable amount of time for the parsing to start before refetching
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
        });
      }, 1000);
    },
  });

  const { mutateAsync: syncFolder, isPending: isSyncingFolder } = useMutation({
    mutationFn: async (folderUuid: string) => {
      if (!groupUuid || !kitUuid) {
        throw new Error("Group UUID and Kit UUID are required");
      }

      await groupKitFolderControllerSync({
        path: {
          groupUuid,
          kitUuid,
          folderUuid,
        },
      });
    },
    onSettled: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolders(groupUuid!, kitUuid!),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolder(groupUuid!, kitUuid!, folderUuid!),
        });
      }, 1000);
    },
  });

  const files = data?.folders
    ? Object.values(data.folders)
        .map((folder) => folder.files)
        .flat()
    : (data?.folder.files ?? []);

  const isAnyFilePending = files.some(fileNeedsStatusPolling);

  const folders = useMemo(
    () => (data?.folders ? Object.values(data.folders) : []),
    [data],
  );

  return {
    folder: data?.folder ?? null,
    files,
    folders,
    foldersMap: data?.folders ?? {},
    getSubFolder(uuid: string) {
      return data?.folders ? (data.folders[uuid] ?? null) : null;
    },
    getSubfolders(uuid: string | null) {
      if (!data?.folders) {
        return [];
      }
      return Object.values(data.folders).filter(
        (folder) => folder.parentFolderUuid === uuid,
      );
    },
    isLoading: isFolderLoading,
    parseRemainingFiles,
    createFile,
    isCreatingFile,
    replaceFile,
    isReplacingFile,
    deleteFile,
    updateFile,
    syncFolder,
    isSyncingFolder,
    isAnyFilePending,
  };
}
